(() => {
  const PRODUCT_LIMIT = 7;
  const COLLECTION_LIMIT = 7;
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));

  const resolveImage = (entity) => {
    const candidate = entity?.image ?? entity?.featured_image ?? entity?.images?.[0];
    if (!candidate) return '';
    if (typeof candidate === 'string') return candidate;
    return candidate.url || candidate.src || '';
  };

  const formatMoney = (priceString, format) => {
    const cents = Math.round(parseFloat(priceString || '0') * 100);
    if (Number.isNaN(cents)) return '';
    const template = format || '${{amount}}';
    const noDecimals = Math.round(cents / 100).toString();
    const withDecimals = (cents / 100).toFixed(2);
    const withComma = withDecimals.replace('.', ',');
    return template
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma);
  };

  const init = (root) => {
    if (!root || root.dataset.predictiveSearchInitialized === 'true') return;
    root.dataset.predictiveSearchInitialized = 'true';

    const input = root.querySelector('[data-predictive-search-input]');
    const resultsEl = root.querySelector('[data-predictive-search-results]');
    if (!input || !resultsEl) return;

    const rootUrl = (root.dataset.rootUrl || '/').replace(/\/$/, '');
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    let debounceTimer = null;
    let activeController = null;
    let currentQuery = '';

    const closeResults = () => {
      resultsEl.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };

    const openResults = () => {
      resultsEl.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const renderLoading = () => {
      resultsEl.innerHTML = '<p class="predictive-search__loading">Searching…</p>';
      openResults();
    };

    const renderEmpty = (term) => {
      resultsEl.innerHTML = `<p class="predictive-search__empty">No products or collections found for "${escapeHtml(term)}".</p>`;
      openResults();
    };

    const productRow = (product) => {
      const image = resolveImage(product);
      const thumb = image
        ? `<span class="predictive-search__thumb"><img src="${escapeHtml(image)}" alt="" loading="lazy" width="44" height="44"></span>`
        : '<span class="predictive-search__thumb"></span>';
      const price = formatMoney(product.price, moneyFormat);
      const comparePrice = parseFloat(product.compare_at_price_max) > parseFloat(product.price_max)
        ? formatMoney(product.compare_at_price_max, moneyFormat)
        : '';
      const priceHtml = `<ins>${escapeHtml(price)}</ins>` + (comparePrice ? `<s>${escapeHtml(comparePrice)}</s>` : '');
      return `
        <a class="predictive-search__item" href="${escapeHtml(product.url)}">
          ${thumb}
          <span class="predictive-search__body">
            <span class="predictive-search__title">${escapeHtml(product.title)}</span>
            <span class="predictive-search__price">${priceHtml}</span>
          </span>
        </a>`;
    };

    const collectionRow = (collection) => {
      const image = resolveImage(collection);
      const thumb = image
        ? `<span class="predictive-search__thumb predictive-search__thumb--collection"><img src="${escapeHtml(image)}" alt="" loading="lazy" width="44" height="44"></span>`
        : '<span class="predictive-search__thumb predictive-search__thumb--collection"></span>';
      return `
        <a class="predictive-search__item" href="${escapeHtml(collection.url)}">
          ${thumb}
          <span class="predictive-search__body">
            <span class="predictive-search__title">${escapeHtml(collection.title)}</span>
          </span>
        </a>`;
    };

    const renderResults = (term, products, collections) => {
      if (products.length === 0 && collections.length === 0) {
        renderEmpty(term);
        return;
      }

      let html = '';

      if (products.length > 0) {
        html += `
          <div class="predictive-search__group">
            <p class="predictive-search__group-title">Products</p>
            ${products.slice(0, PRODUCT_LIMIT).map(productRow).join('')}
          </div>`;
      }

      if (collections.length > 0) {
        html += `
          <div class="predictive-search__group">
            <p class="predictive-search__group-title">Collections</p>
            ${collections.slice(0, COLLECTION_LIMIT).map(collectionRow).join('')}
          </div>`;
      }

      html += `<a class="predictive-search__view-all" href="/search?q=${encodeURIComponent(term)}">View all results for "${escapeHtml(term)}"</a>`;

      resultsEl.innerHTML = html;
      openResults();
    };

    const fetchResults = async (term) => {
      if (activeController) activeController.abort();
      activeController = new AbortController();

      renderLoading();

      const url = `${rootUrl}/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=${Math.max(PRODUCT_LIMIT, COLLECTION_LIMIT)}&resources[options][unavailable_products]=last`;

      try {
        const response = await fetch(url, {
          signal: activeController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Predictive search request failed');
        const data = await response.json();
        if (term !== currentQuery) return;
        const products = data?.resources?.results?.products || [];
        const collections = data?.resources?.results?.collections || [];
        renderResults(term, products, collections);
      } catch (error) {
        if (error.name === 'AbortError') return;
        resultsEl.innerHTML = '<p class="predictive-search__empty">Something went wrong. Please try again.</p>';
        openResults();
      }
    };

    input.addEventListener('input', () => {
      const term = input.value.trim();
      currentQuery = term;
      clearTimeout(debounceTimer);

      if (term.length < MIN_CHARS) {
        closeResults();
        return;
      }

      debounceTimer = setTimeout(() => fetchResults(term), DEBOUNCE_MS);
    });

    input.addEventListener('focus', () => {
      if (currentQuery.length >= MIN_CHARS && resultsEl.innerHTML.trim() !== '') {
        openResults();
      }
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeResults();
        input.blur();
      }
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeResults();
    });
  };

  const initAll = () => {
    document.querySelectorAll('[data-predictive-search]').forEach(init);
  };

  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
